// src/components/Leave/HrDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiRefreshCw, FiUsers, FiClock, FiCheckCircle, FiXCircle,
  FiAlertCircle, FiTrendingUp, FiBriefcase, FiCalendar, FiEye,
  FiChevronDown, FiChevronUp
} from 'react-icons/fi';
import { useAppContext } from '../../context/AppContext';
import StatusBadge from '../Common/StatusBadge';

const HrDashboard = () => {
  const navigate = useNavigate();
  const { leave } = useAppContext();
  const { getHrDashboard, approveLeave, rejectLeave, loading } = leave;

  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // ✅ NEW: Toggle sections
  const [showApproved, setShowApproved] = useState(false);
  const [showRejected, setShowRejected] = useState(false);

  const loadDashboard = async () => {
    setError(null);
    try {
      const data = await getHrDashboard();

      setDashboard(data);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    }
  };

  useEffect(() => {
    loadDashboard();
    const interval = setInterval(loadDashboard, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboard();
    setRefreshing(false);
  };

  // Handle Approve
  const handleApprove = async (leaveId) => {
    if (!window.confirm('Are you sure you want to approve this leave?')) return;

    setProcessingId(leaveId);
    try {
      await approveLeave(leaveId, {
        action: 'approve',
        remarks: 'Approved via HR Dashboard'
      });
      await loadDashboard();
    } catch (err) {
      alert('Failed to approve leave: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setProcessingId(null);
    }
  };

  // Handle Reject
  const handleRejectClick = (leave) => {
    setSelectedLeave(leave);
    setShowRejectModal(true);
    setRejectReason('');
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    setProcessingId(selectedLeave.id);
    try {
      await rejectLeave(selectedLeave.id, {
        action: 'reject',
        reason: rejectReason.trim()
      });
      setShowRejectModal(false);
      await loadDashboard();
    } catch (err) {
      alert('Failed to reject leave: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setProcessingId(null);
      setSelectedLeave(null);
    }
  };

  // Handle View All Pending
  const handleViewAllPending = () => {
    navigate('/leave/admin/pending');
  };

  // Handle Review button
  const handleReview = (leaveId) => {
    navigate(`/leave/${leaveId}`);
  };

  // ✅ NEW: Toggle sections
  const toggleApproved = () => setShowApproved(!showApproved);
  const toggleRejected = () => setShowRejected(!showRejected);

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
        <button onClick={loadDashboard} className="ml-3 text-sm font-medium hover:underline">
          Retry
        </button>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="text-center py-12 text-gray-500">
        No dashboard data available
      </div>
    );
  }

  const { statistics, pending_requests, today_leaves, low_balance, monthly_trend, department_wise } = dashboard;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">HR Leave Dashboard</h1>
          <p className="text-gray-500">Overview of all leave requests and statistics</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
        >
          <FiRefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-50 text-yellow-600 flex items-center justify-center">
              <FiClock className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-gray-800">{statistics?.pending || 0}</p>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center">
              <FiCheckCircle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Approved Today</p>
              <p className="text-2xl font-bold text-gray-800">{statistics?.approved_today || 0}</p>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
              <FiXCircle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Rejected Today</p>
              <p className="text-2xl font-bold text-gray-800">{statistics?.rejected_today || 0}</p>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <FiUsers className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-gray-500">On Leave Today</p>
              <p className="text-2xl font-bold text-gray-800">{today_leaves?.length || 0}</p>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
              <FiAlertCircle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Expired</p>
              <p className="text-2xl font-bold text-gray-800">{statistics?.expired || 0}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending Requests */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Pending Requests</h3>
            <button
              onClick={handleViewAllPending}
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
            >
              View All →
            </button>
          </div>

          {pending_requests?.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No pending requests</p>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {pending_requests?.slice(0, 5).map((leave) => (
                <div
                  key={leave.id}
                  className="flex flex-col p-3 border border-gray-100 rounded-lg hover:border-indigo-200 transition"
                >
                  {/* Employee Info */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-semibold text-sm">
                        {leave.employee_name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <span className="font-medium text-gray-800">
                          {leave.employee_name || 'Unknown'}
                        </span>
                        <span className="text-xs text-gray-400 ml-2">
                          #{leave.employee_id}
                        </span>
                      </div>
                    </div>
                    <StatusBadge status={leave.status} />
                  </div>

                  {/* Leave Details */}
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                    <span>{leave.leave_type_display}</span>
                    <span>•</span>
                    <span>{leave.start_date} → {leave.end_date}</span>
                    <span>•</span>
                    <span>{leave.total_days} days</span>
                    <span className="text-xs text-gray-400">{leave.department}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => handleReview(leave.id)}
                      className="px-3 py-1.5 text-sm text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition flex items-center gap-1"
                    >
                      <FiEye className="w-3 h-3" />
                      Review
                    </button>
                    <button
                      onClick={() => handleApprove(leave.id)}
                      disabled={processingId === leave.id}
                      className="px-3 py-1.5 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition disabled:opacity-50 flex items-center gap-1"
                    >
                      <FiCheckCircle className="w-3 h-3" />
                      {processingId === leave.id ? 'Processing...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => handleRejectClick(leave)}
                      disabled={processingId === leave.id}
                      className="px-3 py-1.5 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition disabled:opacity-50 flex items-center gap-1"
                    >
                      <FiXCircle className="w-3 h-3" />
                      {processingId === leave.id ? 'Processing...' : 'Reject'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Today's Leave */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Today's Leave</h3>
          {today_leaves?.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No one on leave today</p>
          ) : (
            <div className="space-y-3">
              {today_leaves?.map((leave) => (
                <div key={leave.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-800">
                      {leave.employee_name || 'Unknown'}
                    </p>
                    <p className="text-sm text-gray-500">#{leave.employee_id}</p>
                  </div>
                  <StatusBadge status={leave.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ✅ NEW: Approved Requests Section */}
      {dashboard.approved_requests && dashboard.approved_requests.length > 0 && (
        <div className="bg-white rounded-xl border border-green-200 p-6">
          <button
            onClick={toggleApproved}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <FiCheckCircle className="w-5 h-5 text-green-500" />
              <h3 className="text-lg font-semibold text-gray-800">
                Approved Requests ({dashboard.approved_requests.length})
              </h3>
            </div>
            {showApproved ? <FiChevronUp className="w-5 h-5 text-gray-500" /> : <FiChevronDown className="w-5 h-5 text-gray-500" />}
          </button>

          {showApproved && (
            <div className="mt-4 space-y-3 max-h-[400px] overflow-y-auto">
              {dashboard.approved_requests.map((leave) => (
                <div key={leave.id} className="p-3 border border-green-100 rounded-lg bg-green-50/30">
                  {/* Employee Info */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-semibold text-sm">
                        {leave.employee_name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <span className="font-medium text-gray-800">
                          {leave.employee_name || 'Unknown'}
                        </span>
                        <span className="text-xs text-gray-400 ml-2">
                          #{leave.employee_id}
                        </span>
                      </div>
                    </div>
                    <StatusBadge status={leave.status} />
                  </div>

                  {/* Leave Details */}
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                    <span>{leave.leave_type_display}</span>
                    <span>•</span>
                    <span>{leave.start_date} → {leave.end_date}</span>
                    <span>•</span>
                    <span>{leave.total_days} days</span>
                    <span className="text-xs text-gray-400">{leave.department}</span>
                  </div>

                  {/* ✅ NEW: Approval Info */}
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span className="text-green-600">
                      Approved by: {leave.approved_by_name || 'HR'}
                    </span>
                    <span className="text-gray-400">
                      {leave.approved_at ? new Date(leave.approved_at).toLocaleDateString() : ''}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => handleReview(leave.id)}
                      className="px-3 py-1.5 text-sm text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition flex items-center gap-1"
                    >
                      <FiEye className="w-3 h-3" />
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ✅ NEW: Rejected Requests Section */}
      {dashboard.rejected_requests && dashboard.rejected_requests.length > 0 && (
        <div className="bg-white rounded-xl border border-red-200 p-6">
          <button
            onClick={toggleRejected}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <FiXCircle className="w-5 h-5 text-red-500" />
              <h3 className="text-lg font-semibold text-gray-800">
                Rejected Requests ({dashboard.rejected_requests.length})
              </h3>
            </div>
            {showRejected ? <FiChevronUp className="w-5 h-5 text-gray-500" /> : <FiChevronDown className="w-5 h-5 text-gray-500" />}
          </button>

          {showRejected && (
            <div className="mt-4 space-y-3 max-h-[400px] overflow-y-auto">
              {dashboard.rejected_requests.map((leave) => (
                <div key={leave.id} className="p-3 border border-red-100 rounded-lg bg-red-50/30">
                  {/* Employee Info */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-semibold text-sm">
                        {leave.employee_name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <span className="font-medium text-gray-800">
                          {leave.employee_name || 'Unknown'}
                        </span>
                        <span className="text-xs text-gray-400 ml-2">
                          #{leave.employee_id}
                        </span>
                      </div>
                    </div>
                    <StatusBadge status={leave.status} />
                  </div>

                  {/* Leave Details */}
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                    <span>{leave.leave_type_display}</span>
                    <span>•</span>
                    <span>{leave.start_date} → {leave.end_date}</span>
                    <span>•</span>
                    <span>{leave.total_days} days</span>
                    <span className="text-xs text-gray-400">{leave.department}</span>
                  </div>

                  {/* ✅ NEW: Rejection Reason */}
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-700">Rejection Reason:</p>
                    <p className="text-sm text-red-600">{leave.rejection_reason || 'No reason provided'}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => handleReview(leave.id)}
                      className="px-3 py-1.5 text-sm text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition flex items-center gap-1"
                    >
                      <FiEye className="w-3 h-3" />
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Low Balance Alert */}
      {low_balance && low_balance.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <FiAlertCircle className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-semibold text-amber-800">Low Balance Alert</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {low_balance.map((emp, index) => (
              <div key={index} className="bg-white rounded-lg p-4 border border-amber-200">
                <p className="font-medium text-gray-800">{emp.employee_name}</p>
                <p className="text-sm text-gray-500">#{emp.employee_id} • {emp.department}</p>
                <div className="flex gap-4 mt-2 text-sm">
                  <span className="text-amber-600">Casual: {emp.casual_remaining}</span>
                  <span className="text-amber-600">Sick: {emp.sick_remaining}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly Trend */}
      {monthly_trend && Object.keys(monthly_trend).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Monthly Leave Trend</h3>
          <div className="flex items-end gap-3 h-48">
            {Object.entries(monthly_trend).map(([month, count]) => {
              const maxValue = Math.max(...Object.values(monthly_trend), 1);
              const height = (count / maxValue) * 100;
              const monthLabel = month.split('-')[1];
              return (
                <div key={month} className="flex-1 flex flex-col items-center">
                  <div
                    className="w-full bg-indigo-500 rounded-t transition-all duration-500 hover:bg-indigo-600"
                    style={{ height: `${Math.max(height, 2)}%` }}
                  >
                    <div className="text-xs text-white text-center pt-1">{count}</div>
                  </div>
                  <span className="text-xs text-gray-500 mt-2">{monthLabel}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Department Wise */}
      {department_wise && Object.keys(department_wise).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Department-wise Leave Report</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(department_wise).map(([dept, data]) => (
              <div key={dept} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-800">{dept}</h4>
                <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                  <div>
                    <p className="text-gray-500">Employees</p>
                    <p className="font-bold text-gray-800">{data.total_employees}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Leaves</p>
                    <p className="font-bold text-gray-800">{data.leaves_taken}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Avg</p>
                    <p className="font-bold text-gray-800">{data.average_per_employee}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-2">Reject Leave Request</h3>
            <p className="text-gray-500 text-sm mb-4">Please provide a reason for rejection</p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason *
              </label>
              <textarea
                rows="4"
                placeholder="Explain why this leave is being rejected..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim() || processingId === selectedLeave?.id}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                {processingId === selectedLeave?.id ? 'Processing...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HrDashboard;